import { Base, createApp, importWhenVisible } from '@studiometa/js-toolkit';
import { Action, DataComputed, DataEffect, DataModel, ScrollAnimationParent } from '@studiometa/ui';
import { isDev } from './config.js';
import Header from './components/Header.js';
import Listing from './components/Listing.js';
import Hero from './components/Hero.js';
import ArchiveNav from './components/ArchiveNav.js';
import TextAppear from './components/TextAppear.js';
import Form from './components/Form.js';
import StickyWidget from './components/StickyWidget.js';
import Figure from './components/Figure.js';

/**
 * @typedef {{
 *   $children: {
 *     Figure: Figure[],
 *     Video: Array<Promise<import('./components/Video.js').default>>
 *     Accordion: Array<Promise<import('./components/Accordion.js').default>>
 *     Slider: Array<Promise<import('./components/Slider.js').default>>
 *   }
 * }} AppProps
 */

/**
 * App class.
 * @augments {Base<AppProps>}
 */
class App extends Base {
  /**
   * App config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'App',
    log: isDev(),
    components: {
      Figure,
      ScrollAnimationParent,
      Header,
      DataComputed,
      DataModel,
      DataEffect,
      StickyWidget,
      Hero,
      Form,
      Listing,
      Action,
      '.type-animate': TextAppear,
      Map: (app) => importWhenVisible(() => import('./components/Map/index.js'), 'Map', app),
      Calendly: (app) =>
        importWhenVisible(() => import('./components/Calendly.js'), 'Calendly', app),
      ArchiveNav,
      ImageTextGallery: (app) =>
        importWhenVisible(
          () => import('./components/ImageTextGallery.js'),
          'ImageTextGallery',
          app,
        ),
      PushHover: (app) =>
        importWhenVisible(() => import('./components/PushHover.js'), 'PushHover', app),
      StickyCards: (app) =>
        importWhenVisible(() => import('./components/StickyCards.js'), 'StickyCards', app),
      Slider: (app) => importWhenVisible(() => import('./components/Slider.js'), 'Slider', app),
      AnimateSlider: (app) =>
        importWhenVisible(() => import('./components/AnimateSlider.js'), 'AnimateSlider', app),
      Video: (app) => importWhenVisible(() => import('./components/Video.js'), 'Video', app),
      Accordion: (app) =>
        importWhenVisible(() => import('./components/Accordion.js'), 'Accordion', app),
      TableOfContents: (app) =>
        importWhenVisible(() => import('./components/TableOfContents.js'), 'TableOfContents', app),
    },
  };
}

export default createApp(App, document.body);
